import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiFetch } from '../utils/api';
import PageHeader, { btnPrimary, cardClass } from '../components/PageHeader';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadUsers = () => {
    setLoading(true);
    apiFetch('/admin/users')
      .then((r) => setUsers(r.data || []))
      .catch((err) => toast.error(err.message || 'Failed to load users'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const resendInvite = async (inviteId) => {
    try {
      const res = await apiFetch(`/admin/users/invites/${inviteId}/send-invite`, { method: 'POST' });
      if (res.data?.email_sent) {
        toast.success(`Invite email sent to ${res.data.email}`);
      } else {
        toast.success(res.message || 'Invite link ready');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    }
  };

  const cancelInvite = async (inviteId) => {
    if (!window.confirm('Cancel this pending invite?')) return;
    try {
      await apiFetch(`/admin/users/invites/${inviteId}`, { method: 'DELETE' });
      toast.success('Invite cancelled');
      loadUsers();
    } catch (err) {
      toast.error(err.message || 'Failed to cancel invite');
    }
  };

  const toggleActive = async (user) => {
    const next = !user.is_active;
    const label = next ? 'activate' : 'deactivate';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${user.email}?`)) return;
    try {
      await apiFetch(`/admin/users/${user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: next }),
      });
      toast.success(`User ${label}d`);
      loadUsers();
    } catch (err) {
      toast.error(err.message || `Failed to ${label} user`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Admin users"
        subtitle="Invite teammates to the admin portal — they set their own password via email"
        action={
          <Link to="/users/new" className={btnPrimary}>
            Invite admin
          </Link>
        }
      />

      <div className={cardClass}>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Added</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={`${u.status}-${u.id}`} className="border-t border-slate-100">
                  <td className="px-5 py-3 font-medium">{u.name || '—'}</td>
                  <td className="px-5 py-3">{u.email}</td>
                  <td className="px-5 py-3">
                    {u.status === 'active' ? (
                      <span className={u.is_active ? 'text-green-700' : 'text-slate-500'}>
                        {u.is_active ? 'Active' : 'Inactive'}
                        {u.must_change_password && u.is_active ? (
                          <span className="text-amber-700 ml-2">· must change password</span>
                        ) : null}
                      </span>
                    ) : (
                      <span className="text-amber-700">Invite pending</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-slate-600">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-5 py-3 text-right space-x-3">
                    {u.status === 'invite_pending' ? (
                      <>
                        <button
                          type="button"
                          onClick={() => resendInvite(u.invite_id || u.id)}
                          className="text-teal-700 hover:underline"
                        >
                          Resend invite
                        </button>
                        <button
                          type="button"
                          onClick={() => cancelInvite(u.invite_id || u.id)}
                          className="text-red-700 hover:underline"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleActive(u)}
                        className="text-teal-700 hover:underline"
                      >
                        {u.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
            {!loading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-8 text-center text-slate-500">
                  No admin users yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
